import { useCallback, useRef, useState } from 'react';
import { MAX_INTEL_INCREASE_PULSE_DURATION_MS } from '../config/maxIntelIncreasePulseEffect';

export function mapMaxIntelIncreasePulseForViewer(entry, viewerUserId) {
  if (!entry?.playerId) {
    throw new Error('mapMaxIntelIncreasePulseForViewer: playerId is required');
  }
  if (viewerUserId == null) {
    throw new Error('mapMaxIntelIncreasePulseForViewer: viewerUserId is required');
  }
  const isOwnerViewer = String(entry.playerId) === String(viewerUserId);
  return {
    generalZone: isOwnerViewer ? 'player_general' : 'opponent_general',
  };
}

/**
 * Blue chevron pulse on the benefiting player's general portrait when max intel increases.
 */
export function useMaxIntelIncreasePulse() {
  const [activePulses, setActivePulses] = useState([]);
  const completedRef = useRef(new Set());
  const pendingTimersRef = useRef(new Set());

  const registerPulse = useCallback((payload) => {
    if (!payload?.generalZone) {
      throw new Error('useMaxIntelIncreasePulse.registerPulse: generalZone is required');
    }

    const animationKey = payload.animationKey || `max-intel-${payload.generalZone}-${Date.now()}`;
    if (completedRef.current.has(animationKey)) {
      return;
    }

    setActivePulses((prev) => {
      if (prev.some((p) => p.key === animationKey)) {
        return prev;
      }
      return [
        ...prev,
        {
          key: animationKey,
          generalZone: payload.generalZone,
          animationKey,
        },
      ];
    });

    const timerId = window.setTimeout(() => {
      pendingTimersRef.current.delete(timerId);
      completedRef.current.add(animationKey);
      setActivePulses((prev) => prev.filter((p) => p.key !== animationKey));
    }, MAX_INTEL_INCREASE_PULSE_DURATION_MS + 120);
    pendingTimersRef.current.add(timerId);
  }, []);

  const schedulePulse = useCallback((payload, delayMs = 0) => {
    const safeDelay = Number.isFinite(delayMs) && delayMs >= 0 ? delayMs : 0;
    const timerId = window.setTimeout(() => {
      pendingTimersRef.current.delete(timerId);
      registerPulse(payload);
    }, safeDelay);
    pendingTimersRef.current.add(timerId);
  }, [registerPulse]);

  const reset = useCallback(() => {
    pendingTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    pendingTimersRef.current.clear();
    completedRef.current.clear();
    setActivePulses([]);
  }, []);

  return {
    activePulses,
    registerPulse,
    schedulePulse,
    reset,
  };
}

export default useMaxIntelIncreasePulse;
